import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { auth, db, firebaseService, COLLECTIONS } from './firebase';

export interface HackerChecklistItem {
  id: string;
  text: string;
  referenceUrl?: string;
  toolHint?: string;
  required?: boolean;
}

export interface HackerChecklistSection {
  id: string;
  title: string;
  objective?: string;
  items: HackerChecklistItem[];
}

export type HackerChecklistStatus = 'Draft' | 'Published';
export type HackerChecklistDifficulty = 'Beginner' | 'Intermediate' | 'Advanced';

export interface HackerChecklist {
  id: string;
  title: string;
  description?: string;
  category?: string;
  tags: string[];
  status: HackerChecklistStatus;
  difficulty?: HackerChecklistDifficulty;
  sections: HackerChecklistSection[];
  createdAt: string;
  updatedAt: string;
}

export interface HackerChecklistInput extends Omit<HackerChecklist, 'id' | 'createdAt' | 'updatedAt'> {}

export interface ChecklistProgress {
  checklistId: string;
  userId: string;
  completedItemIds: string[];
  notes?: string;
  updatedAt: string;
}

const toIsoString = (value?: Timestamp | string | null): string => {
  if (!value) return new Date().toISOString();
  if (typeof value === 'string') return value;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  return new Date().toISOString();
};

const convertChecklistDoc = (docSnap: QueryDocumentSnapshot<DocumentData>): HackerChecklist => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    title: data.title || 'Untitled Checklist',
    description: data.description || '',
    category: data.category || 'general',
    tags: Array.isArray(data.tags) ? data.tags : [],
    status: data.status === 'Published' ? 'Published' : 'Draft',
    difficulty: data.difficulty,
    sections: Array.isArray(data.sections) ? data.sections : [],
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt)
  };
};

const getChecklistCollection = () => collection(db, COLLECTIONS.HACKER_CHECKLISTS);
const getProgressCollection = () => collection(db, COLLECTIONS.USER_CHECKLIST_PROGRESS);

export const hackerChecklistService = {
  getChecklists: async (opts?: { status?: HackerChecklistStatus }): Promise<HackerChecklist[]> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn('Firebase not initialized, returning empty checklist array');
      return [];
    }

    const q = query(getChecklistCollection(), orderBy('updatedAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(convertChecklistDoc)
      .filter(checklist => !opts?.status || checklist.status === opts.status);
  },

  subscribeToChecklists: (
    callback: (checklists: HackerChecklist[]) => void,
    opts?: { status?: HackerChecklistStatus }
  ) => {
    if (!firebaseService.isInitialized || !db) {
      console.warn('Firebase not initialized, returning empty checklist array');
      callback([]);
      return () => {};
    }

    const q = query(getChecklistCollection(), orderBy('updatedAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const records = snapshot.docs
        .map(convertChecklistDoc)
        .filter(checklist => !opts?.status || checklist.status === opts.status);
      callback(records);
    });
  },

  getChecklistById: async (id: string): Promise<HackerChecklist | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn('Firebase not initialized, cannot load checklist');
      return null;
    }

    const docRef = doc(db, COLLECTIONS.HACKER_CHECKLISTS, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return convertChecklistDoc(docSnap as QueryDocumentSnapshot<DocumentData>);
  },

  createChecklist: async (input: HackerChecklistInput): Promise<string | null> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn('Firebase not initialized, cannot create checklist');
      return null;
    }

    const data = {
      ...input,
      tags: input.tags || [],
      sections: input.sections || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(getChecklistCollection(), data);
    return docRef.id;
  },

  updateChecklist: async (id: string, updates: Partial<HackerChecklistInput>): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn('Firebase not initialized, cannot update checklist');
      return false;
    }

    const docRef = doc(db, COLLECTIONS.HACKER_CHECKLISTS, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  },

  deleteChecklist: async (id: string): Promise<boolean> => {
    if (!firebaseService.isInitialized || !db) {
      console.warn('Firebase not initialized, cannot delete checklist');
      return false;
    }

    const docRef = doc(db, COLLECTIONS.HACKER_CHECKLISTS, id);
    await deleteDoc(docRef);
    return true;
  },

  getUserProgress: async (checklistId: string): Promise<ChecklistProgress | null> => {
    if (!firebaseService.isInitialized || !db || !auth?.currentUser) {
      return null;
    }

    const userId = auth.currentUser.uid;
    const docRef = doc(db, COLLECTIONS.USER_CHECKLIST_PROGRESS, `${userId}_${checklistId}`);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      checklistId,
      userId,
      completedItemIds: Array.isArray(data.completedItemIds) ? data.completedItemIds : [],
      notes: data.notes,
      updatedAt: toIsoString(data.updatedAt)
    };
  },

  saveUserProgress: async (checklistId: string, completedItemIds: string[], notes?: string): Promise<void> => {
    if (!firebaseService.isInitialized || !db || !auth?.currentUser) {
      throw new Error('User must be authenticated to save checklist progress');
    }

    const userId = auth.currentUser.uid;
    const docRef = doc(db, COLLECTIONS.USER_CHECKLIST_PROGRESS, `${userId}_${checklistId}`);
    await setDoc(docRef, {
      checklistId,
      userId,
      completedItemIds,
      notes: notes || '',
      updatedAt: serverTimestamp()
    });
  }
};

export type { HackerChecklist as HackerChecklistRecord };

